import * as moment from "moment-timezone";
import * as React from "react";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { getDay } from "src/core/orders/utils/order-deliveries";
import { useDrop } from "src/core/drops/hooks";
import {
  IProduct,
  ProductStatus
} from "src/core/products/api/products.api";
import { WeekDay } from "src/core/drops/api/drops.api";
import { useProductLeadTimeGroup } from "src/core/products/hooks/useProductLeadTimeGroup";
import { useProductIsAllowedSubs } from "src/core/products/hooks/useProductIsAllowedSubs";

import { IOrderLine } from "../../api/orders.api";
import useCutoffTimeForWeekDay from "./useCutoffTimeForWeekDay";
import useDaysToCutoff from "./useDaysToCutoff";

/**
 * Check if a product is available on a certain order day
 *
 * @param {IOrder} order - order object.
 * @param {IDrop} drop - drop object.
 *
 * @return {string | null} - The reason it didn't pass, or null if it does pass.
 */
const useCanOrderProduct = (product: IProduct, deliveryDate: Date | null, orderLine: IOrderLine) => {
  const [ { drop } ] = useDrop();
  const cutOffTimeForWeekDay = useCutoffTimeForWeekDay();
  const daysToCutoff = useDaysToCutoff();
  const productLeadTimeGroup = useProductLeadTimeGroup();
  const isAllowedSubs = useProductIsAllowedSubs(product);

  const canOrderProduct = React.useMemo(() => {
    if (!drop) {
      return false;
    }

    if (orderLine.productSubstitutionId && (!isAllowedSubs || !orderLine.substituteOrderLineId)) {
      return false;
    }

    const weekLeadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);
    const defaultCutoff = getDeliveryCutoff(drop.groupedSettings);
    const leadTimeGroup = productLeadTimeGroup(product);
    const deliveryDOW = moment.utc(deliveryDate || undefined).isoWeekday() as WeekDay;

    // if product suspended, delisted or unpublished
    if (product.currentStatus?.status === ProductStatus.Suspended || product.currentStatus?.status === ProductStatus.Delisted || !product.published) {
      return false;
    }

    // Don't proceed with date-related validation if madeToOrder
    if (product.madeToOrder && deliveryDate) {
      return false;
    }

    if (!product.prices || product.prices.length === 0) {
      return false;
    }

    // made to order
    if (!deliveryDate) {
      return true;
    }

    // delivery route day checks
    if (product.deliveryRoute && product.deliveryRoute[ 0 ]) {
      const deliveryDay = getDay(moment.utc(deliveryDate).isoWeekday());

      if (!deliveryDay || (product.deliveryRoute[ 0 ].deliveryDays && product.deliveryRoute[ 0 ].deliveryDays[ deliveryDay ] === undefined)) {
        return false;
      }
    }

    // lead time cutoff checks
    if (!weekLeadTimes || !defaultCutoff || !leadTimeGroup) {
      return true;
    } else {
      const cutOffTime = cutOffTimeForWeekDay(leadTimeGroup, deliveryDOW);

      if (!cutOffTime) {
        return false;
      }
      const daysToCut = daysToCutoff(deliveryDate, leadTimeGroup);

      if (daysToCut === null || daysToCut < 0) {
        return false;
      } else {
        return true;
      }
    }
  },
  [
    drop,
    orderLine.productSubstitutionId,
    orderLine.substituteOrderLineId,
    isAllowedSubs,
    productLeadTimeGroup,
    product,
    deliveryDate,
    cutOffTimeForWeekDay,
    daysToCutoff
  ]
  );

  return canOrderProduct;
};

export default useCanOrderProduct;
